import { format as formatDate } from 'date-fns';
import {
  formatHumanReadableNumber,
  formatMoney,
} from 'helpers/formatters/number';
import React from 'react';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Coin } from 'store/Coin/types';
import { USD } from 'store/Currency/types';
import { theme } from 'styles';

import CustomTooltip from '../Tooltip';
import { Container, LegendItem, LegendList } from './styles';

enum DataKeys {
  price = 'price',
  date = 'date',
  volume = 'volume',
}

interface Props {
  data: Array<{
    [DataKeys.date]: number;
    [DataKeys.price]: number;
    [DataKeys.volume]: number | null;
  }>;
  coin: Coin;
}

const PriceCustomActiveDot = (props: { cx: number; cy: number }) => {
  const { cx, cy } = props;
  const rectSize = 8;
  const circleRadius = 10;

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={circleRadius}
        fill={theme.charts.colors.priceDot}
      />
      <rect
        x={cx - rectSize / 2}
        y={cy - rectSize / 2}
        width={rectSize}
        height={rectSize}
        fill={theme.charts.colors.priceLine}
        strokeWidth={1}
        stroke={theme.colors.white}
      />
    </g>
  );
};

interface VolumeCustomActiveDotProps {
  cx: number;
  cy: number;
  payload: {
    date: number;
    price: number;
    volume: number | null;
  };
}

const VolumeCustomActiveDot = (props: VolumeCustomActiveDotProps) => {
  const { cx, cy, payload } = props;

  if (payload.volume === null) {
    return null;
  }

  return (
    <g>
      <circle r={4} cx={cx} cy={cy} />
      <circle cx={cx} cy={cy} r={10} fill={theme.charts.colors.xAxisDotGlow} />
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={theme.charts.colors.xAxisDot}
        strokeWidth={1}
        stroke={theme.colors.white}
      />
    </g>
  );
};

const VolumeCustomCursor = (props: any) => {
  const { points } = props;

  if (points.length !== 2) {
    return null;
  }

  return (
    <g>
      <line
        x1={points[0].x}
        y1={points[0].y - 5}
        x2={points[1].x}
        y2={points[1].y}
        stroke={theme.charts.colors.cursor}
      />
    </g>
  );
};

const Legend = () => {
  const { t } = useTranslation();

  return (
    <LegendList>
      <LegendItem color={theme.charts.colors.priceLine}>
        {t('assets.detail.charts.price-usd')}
      </LegendItem>
      <LegendItem color={theme.charts.colors.volumeBar}>
        {t('assets.detail.charts.24h-volume')}
      </LegendItem>
    </LegendList>
  );
};

const Chart = (props: Props) => {
  const { data, coin } = props;

  const volumeTooltipContent = useCallback(() => null, []);

  const xAxisTickFormatter = useCallback(
    (value) => {
      if (data.length && value === data[data.length - 1].date) {
        return '';
      }

      if (!value || !isFinite(value)) {
        return '';
      }

      return formatDate(new Date(value), 'dd/MM');
    },
    [data],
  );

  const yAxisTickFormatter = useCallback(
    (value) =>
      value === 0
        ? 0
        : formatHumanReadableNumber(value * (coin.priceInUSD || 1), {
            currency: USD,
            formatThousands: value >= 100000,
          }),
    [coin.priceInUSD],
  );

  const yAxisTickFormatterPrice = useCallback(
    (value) => formatMoney(value, USD),
    [],
  );

  return (
    <Container>
      <ResponsiveContainer width="100%" height={250}>
        <LineChart
          syncId={1}
          data={data}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        >
          <CartesianGrid
            strokeDasharray="1 2"
            stroke={theme.charts.colors.grid}
            vertical={false}
          />
          <Tooltip
            cursor={{ stroke: theme.charts.colors.cursor }}
            content={<CustomTooltip coinCode={coin.code} />}
            animationDuration={0}
          />
          <Line
            type="monotone"
            dataKey={DataKeys.price}
            dot={false}
            strokeWidth={2}
            stroke={theme.charts.colors.priceLine}
            activeDot={PriceCustomActiveDot}
          />
          {/* Needs this transparent axis to set the domain, not able to set domain on the graph itself :( */}
          <XAxis
            dataKey={DataKeys.date}
            type="number"
            domain={['dataMin', 'dataMax']}
            stroke="transparent"
            mirror={true}
          />
          <YAxis
            domain={['dataMin', 'dataMax']}
            orientation="right"
            dataKey={DataKeys.price}
            tickLine={false}
            tickSize={3}
            tickFormatter={yAxisTickFormatterPrice}
            stroke={theme.charts.colors.priceLine}
            axisLine={{ strokeWidth: 0 }}
            padding={{ top: 20, bottom: 20 }}
          />
        </LineChart>
      </ResponsiveContainer>
      <ResponsiveContainer width="100%" height={100}>
        <ComposedChart
          syncId={1}
          data={data}
          margin={{ top: 5, right: 0, bottom: 0, left: 0 }}
        >
          <Bar
            dataKey={DataKeys.volume}
            barSize={20}
            fill={theme.charts.colors.volumeBar}
          />
          {/* This tooltip is only used for the cursor, not to show content */}
          <Tooltip
            animationDuration={0}
            content={volumeTooltipContent}
            cursor={<VolumeCustomCursor />}
          />
          <Line
            dataKey={DataKeys.volume}
            stroke="transparent"
            activeDot={VolumeCustomActiveDot}
            dot={false}
          />
          <XAxis
            dataKey={DataKeys.date}
            type="number"
            domain={['dataMin', 'dataMax']}
            stroke={theme.charts.colors.grid}
            tick={{ fill: theme.colors.dirtyWhite }}
            tickSize={7}
            tickMargin={4}
            tickFormatter={xAxisTickFormatter}
            tickCount={30}
          />
          <YAxis
            dataKey={DataKeys.volume}
            orientation="right"
            axisLine={{ strokeWidth: 0 }}
            tickLine={false}
            tickSize={3}
            padding={{ top: 5, bottom: 0 }}
            tickFormatter={yAxisTickFormatter}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <Legend />
    </Container>
  );
};

export default memo(Chart);
